import React, { useState, useEffect, useCallback } from "react";
import { Card, Button, Table, Space, message, Badge, Dropdown, Tag, Tooltip, Input, Typography, Select, DatePicker, Spin, Divider, Modal } from "antd";
import { EyeOutlined, EditOutlined, DeleteOutlined, MoreOutlined, PlusOutlined, SearchOutlined, ReloadOutlined, DollarOutlined } from "@ant-design/icons";
import { useLoadList } from "../../hooks";
import { useTableSelection } from "../../hooks/useTableSelection";
import { getActionItems, getActionItemById, processActionItem } from "../../api/actionItemApi";
import { formatDate, formatDateTime } from "../../utils/dateUtils";
import { useAuth } from "../../hooks/useAuth";
import PermissionGuard from "../../components/auth/PermissionGuard";
import ActionItemDetailDrawer from "./components/ActionItemDetailDrawer";
import ActionItemEditDrawer from "./components/ActionItemEditDrawer";
import ActionItemCreateDrawer from "./components/ActionItemCreateDrawer";
import ActionItemFilter from "./components/ActionItemFilter";
import { useNavigate } from "react-router-dom";

const { Text } = Typography;

const ActionItemList = () => {
    const navigate = useNavigate();
    // 获取权限相关功能
    const { hasPermission, loading: authLoading, user } = useAuth();

    // 状态定义
    const [filterParams, setFilterParams] = useState({
        status: "PENDING_AI,PENDING_REVIEW,PROCESSING", // 默认显示待处理的项目（包括处理中）
    });
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
    });
    const [detailDrawerVisible, setDetailDrawerVisible] = useState(false);
    const [editDrawerVisible, setEditDrawerVisible] = useState(false);
    const [createDrawerVisible, setCreateDrawerVisible] = useState(false);
    const [selectedActionItem, setSelectedActionItem] = useState(null);

    // 使用自定义Hook加载数据
    const { data, isLoading, refetch } = useLoadList("actionItems", getActionItems, {
        page: pagination.current,
        page_size: pagination.pageSize,
        ...filterParams,
    });

    // 使用表格选择Hook
    const { selectedRowKeys, selectedRows, tableRef, rowSelection, getRowProps, clearSelection, handleRowSelectionChange, getIndexColumn } = useTableSelection({
        rowKey: "id",
        dataSource: data?.items || [],
        pagination,
    });

    // 全选当前页面所有项目
    const handleSelectAllVisible = useCallback(() => {
        const visibleKeys = (data?.items || []).map((item) => item.id);
        handleRowSelectionChange(visibleKeys, data?.items || []);
    }, [data?.items, handleRowSelectionChange]);

    // 自定义行属性函数
    const getCustomRowProps = useCallback(
        (record, index) => {
            const originalProps = getRowProps(record, index);
            const { onClick, ...restProps } = originalProps;
            return {
                ...restProps,
            };
        },
        [getRowProps],
    );

    // 查看详情
    const viewActionItemDetail = useCallback((actionItem) => {
        setSelectedActionItem(actionItem);
        setDetailDrawerVisible(true);
    }, []);

    // 编辑
    const editActionItem = useCallback((actionItem) => {
        setSelectedActionItem(actionItem);
        setEditDrawerVisible(true);
    }, []);

    // 创建新项目
    const createActionItem = useCallback(() => {
        setCreateDrawerVisible(true);
    }, []);

    // 删除确认
    const deleteActionItem = useCallback(async (actionItem) => {
        Modal.confirm({
            title: "确认删除",
            content: `确定要删除行动项 "${actionItem.id}" 吗？此操作不可恢复。`,
            okText: "确认删除",
            cancelText: "取消",
            okType: "danger",
            onOk: async () => {
                try {
                    // 注意：当前API没有删除接口，这里需要后端支持
                    message.warning("删除功能暂未实现，需要后端API支持");
                    // await deleteActionItemById(actionItem.id);
                    // message.success("删除成功");
                    // refetch();
                } catch (error) {
                    const errorMsg = error.response?.data?.detail || error.message || "删除失败";
                    message.error(`删除失败: ${errorMsg}`);
                }
            },
        });
    }, []);

    // 处理表格分页变化
    const handleTableChange = useCallback((newPagination, filters, sorter) => {
        setPagination(newPagination);
    }, []);

    // 处理搜索
    const handleSearch = useCallback(
        (values) => {
            const params = {};

            if (values.search_key) {
                params.search_key = values.search_key;
            }

            if (values.status && values.status.length > 0) {
                if (Array.isArray(values.status)) {
                    params.status = values.status.join(",");
                } else {
                    params.status = values.status;
                }
            }

            if (values.priority !== undefined && values.priority !== null) {
                params.priority = values.priority;
            }

            if (values.source_type) {
                params.source_type = values.source_type;
            }

            if (values.agent_type) {
                params.agent_type = values.agent_type;
            }

            if (values.dateRange && values.dateRange.length === 2) {
                if (values.dateRange[0]) {
                    params.created_after = values.dateRange[0].toISOString();
                }
                if (values.dateRange[1]) {
                    params.created_before = values.dateRange[1].toISOString();
                }
            }

            setFilterParams(params);
            setPagination({
                ...pagination,
                current: 1,
            });
        },
        [pagination],
    );

    // 重置搜索
    const handleReset = useCallback(() => {
        setFilterParams({
            status: "PENDING_AI,PENDING_REVIEW,PROCESSING",
        });
        setPagination({
            ...pagination,
            current: 1,
        });
    }, [pagination]);

    // 处理成功后的刷新
    const handleSuccess = useCallback(() => {
        refetch();
        clearSelection();
    }, [refetch, clearSelection]);

    // 如果认证信息还在加载中，显示加载状态
    if (authLoading) {
        return (
            <div className="page-container">
                <Card className="data-card">
                    <div style={{ textAlign: "center", padding: "50px 0" }}>
                        <Spin size="large" />
                        <p style={{ marginTop: 16 }}>正在加载用户信息...</p>
                    </div>
                </Card>
            </div>
        );
    }

    // 权限检查
    if (!hasPermission("flight_operation:read")) {
        return (
            <div className="page-container">
                <Card className="data-card">
                    <div style={{ textAlign: "center", padding: "50px 0" }}>
                        <h3>无权限访问</h3>
                        <p>您没有查看行动项的权限，请联系管理员。</p>
                        <p style={{ color: "#666", fontSize: "12px", marginTop: 16 }}>
                            当前用户: {user?.username || "未知"} | 权限列表: {user?.permissions?.length ? user.permissions.join(", ") : "无权限"}
                        </p>
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <div className="page-container">
            {/* 页面标题 */}
            <div className="page-header-compact">
                <div className="page-title-compact">AI 行动项管理</div>
                <div className="page-actions">
                    <PermissionGuard permission="flight_operation:update">
                        <Button type="primary" icon={<PlusOutlined />} onClick={createActionItem}>
                            新建行动项
                        </Button>
                    </PermissionGuard>
                </div>
            </div>

            {/* 搜索过滤 */}
            <Card className="filter-card">
                <ActionItemFilter onSearch={handleSearch} onReset={handleReset} />
            </Card>

            {/* 行动项列表 */}
            <Card className="data-card" style={{ position: "relative" }}>
                {/* 批量操作工具栏 */}
                {selectedRowKeys.length > 0 && (
                    <div
                        style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            zIndex: 10,
                            padding: "6px 12px",
                            marginTop: "16px",
                            background: "linear-gradient(90deg, rgba(245, 245, 245, 1) 0%, rgba(245, 245, 245,1) 56%, rgba(245, 245, 245, 0) 60%)",
                            borderRadius: "4px",
                            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                            pointerEvents: "none",
                        }}
                    >
                        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", width: "60%", pointerEvents: "auto" }}>
                            <span style={{ marginRight: 8, fontSize: "12px" }}>
                                已选择 <span style={{ fontWeight: "bold" }}>{selectedRowKeys.length}</span> 个行动项
                            </span>
                            <Divider type="vertical" style={{ margin: "0 4px" }} />
                            <Button size="small" type="link" onClick={handleSelectAllVisible} style={{ padding: "0 4px" }}>
                                全选当前页
                            </Button>
                            <Divider type="vertical" style={{ margin: "0 4px" }} />
                            <Button size="small" type="link" onClick={clearSelection} style={{ padding: "0 4px" }}>
                                取消选择
                            </Button>
                        </div>
                    </div>
                )}

                <Table
                    size="small"
                    columns={getColumns()}
                    dataSource={data?.items || []}
                    rowKey="id"
                    pagination={{
                        ...pagination,
                        total: data?.total || 0,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        showTotal: (total) => `共 ${total} 条记录`,
                    }}
                    loading={isLoading}
                    onChange={handleTableChange}
                    scroll={{ x: 1400 }}
                    rowSelection={
                        hasPermission("flight_operation:update")
                            ? {
                                  ...rowSelection,
                                  columnWidth: 6,
                              }
                            : null
                    }
                    ref={tableRef}
                    onRow={getCustomRowProps}
                />
            </Card>

            {/* 详情抽屉 */}
            <ActionItemDetailDrawer visible={detailDrawerVisible} onClose={() => setDetailDrawerVisible(false)} actionItem={selectedActionItem} />

            {/* 编辑抽屉 */}
            <ActionItemEditDrawer visible={editDrawerVisible} onClose={() => setEditDrawerVisible(false)} actionItem={selectedActionItem} onSuccess={handleSuccess} />

            {/* 创建抽屉 */}
            <ActionItemCreateDrawer visible={createDrawerVisible} onClose={() => setCreateDrawerVisible(false)} onSuccess={handleSuccess} />
        </div>
    );

    // 表格列定义
    function getColumns() {
        return [
            getIndexColumn("序号", 60),
            {
                title: "ID",
                dataIndex: "id",
                key: "id",
                width: 80,
                render: (text) => <Text code>{text}</Text>,
            },
            {
                title: "来源类型",
                dataIndex: "source_type",
                key: "source_type",
                width: 150,
                ellipsis: true,
                render: (text) => (
                    <Tooltip title={text}>
                        <Tag color="blue" size="small">
                            {text}
                        </Tag>
                    </Tooltip>
                ),
            },
            {
                title: "状态",
                dataIndex: "status",
                key: "status",
                width: 120,
                render: (status) => {
                    const statusMap = {
                        PENDING_AI: { status: "processing", text: "待AI处理", color: "orange" },
                        PENDING_REVIEW: { status: "warning", text: "待人工审核", color: "gold" },
                        PROCESSING: { status: "processing", text: "处理中", color: "blue" },
                        COMPLETED: { status: "success", text: "已完成", color: "green" },
                        FAILED: { status: "error", text: "处理失败", color: "red" },
                        REJECTED: { status: "default", text: "已拒绝", color: "gray" },
                    };

                    const statusConfig = statusMap[status] || { status: "default", text: "未知状态", color: "gray" };

                    return (
                        <Tag color={statusConfig.color} size="small">
                            {statusConfig.text}
                        </Tag>
                    );
                },
            },
            {
                title: "优先级",
                dataIndex: "priority",
                key: "priority",
                width: 80,
                render: (priority) => {
                    if (priority === null || priority === undefined) return "-";
                    const priorityMap = {
                        1: { text: "高", color: "red" },
                        2: { text: "中", color: "orange" },
                        3: { text: "低", color: "green" },
                    };
                    const config = priorityMap[priority] || { text: priority, color: "default" };
                    return (
                        <Tag color={config.color} size="small">
                            {config.text}
                        </Tag>
                    );
                },
            },
            {
                title: "Agent类型",
                dataIndex: "agent_type",
                key: "agent_type",
                width: 150,
                ellipsis: true,
                render: (text) =>
                    text ? (
                        <Tag color="purple" size="small">
                            {text}
                        </Tag>
                    ) : (
                        "-"
                    ),
            },
            {
                title: "建议变更类型",
                dataIndex: "suggested_change_type",
                key: "suggested_change_type",
                width: 150,
                ellipsis: true,
                render: (text) =>
                    text ? (
                        <Tag color="cyan" size="small">
                            {text}
                        </Tag>
                    ) : (
                        "-"
                    ),
            },
            {
                title: "解决状态",
                dataIndex: "resolution",
                key: "resolution",
                width: 120,
                render: (resolution) => {
                    if (!resolution) return "-";
                    const resolutionMap = {
                        AUTO_EXECUTED: { text: "自动执行", color: "green" },
                        USER_CONFIRMED: { text: "用户确认", color: "blue" },
                        USER_MODIFIED: { text: "用户修改", color: "orange" },
                        USER_REJECTED: { text: "用户拒绝", color: "red" },
                    };
                    const config = resolutionMap[resolution] || { text: resolution, color: "default" };
                    return (
                        <Tag color={config.color} size="small">
                            {config.text}
                        </Tag>
                    );
                },
            },
            {
                title: "创建时间",
                dataIndex: "created_at",
                key: "created_at",
                width: 180,
                render: (text) => (text ? formatDateTime(text) : "未知"),
            },
            {
                title: "更新时间",
                dataIndex: "updated_at",
                key: "updated_at",
                width: 180,
                render: (text) => (text ? formatDateTime(text) : "未知"),
            },
            {
                title: "操作",
                key: "action",
                fixed: "right",
                width: 220,
                render: (_, record) => (
                    <Space size="small" className="table-actions">
                        <PermissionGuard permission="flight_operation:read">
                            <Button
                                type="link"
                                size="small"
                                icon={<EyeOutlined />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    viewActionItemDetail(record);
                                }}
                            >
                                详情
                            </Button>
                        </PermissionGuard>
                        <PermissionGuard permission="flight_operation:update">
                            <Button
                                type="link"
                                size="small"
                                icon={<EditOutlined />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    editActionItem(record);
                                }}
                            >
                                编辑
                            </Button>
                        </PermissionGuard>
                        <PermissionGuard permission="flight_operation:read">
                            <Button
                                type="link"
                                size="small"
                                icon={<DollarOutlined />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigate("/settlement/ap-workbench");
                                }}
                            >
                                结算
                            </Button>
                        </PermissionGuard>
                        <PermissionGuard permission="flight_operation:update">
                            <Dropdown
                                menu={{
                                    items: [
                                        {
                                            key: "delete",
                                            label: "删除",
                                            icon: <DeleteOutlined />,
                                            onClick: () => deleteActionItem(record),
                                            danger: true,
                                        },
                                    ],
                                }}
                                placement="bottomRight"
                                trigger={["click"]}
                            >
                                <Button type="link" size="small" icon={<MoreOutlined />} onClick={(e) => e.stopPropagation()} />
                            </Dropdown>
                        </PermissionGuard>
                    </Space>
                ),
            },
        ];
    }
};

export default ActionItemList;
